import type { ReactNode } from 'react'

/**
 * docs/designs/mobile 的一套原语。刻意放一个文件：它们加起来不到 250 行、
 * 总是一起改（改一个 token 的语义要同时看它们几个），拆成八个文件只是让每次
 * 改动多开七个标签页。真长到看不动了再拆。
 */

/** 稿子的 `.sect`：13px 小标题 + 右侧计数。左右 12px 外边距由它自己带。 */
export function Section({ title, tail, children }: {
  title: ReactNode
  tail?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="mx-3 mt-4">
      <h2 className="flex items-center gap-1.5 m-0 mb-2 px-1 text-[13px] font-bold text-muted">
        {title}
        {tail && <span className="ml-auto font-semibold">{tail}</span>}
      </h2>
      {children}
    </section>
  )
}

/** 稿子的 `.list`：卡片底 + 20px 圆角 + 唯一那一个阴影，子行自己画分隔线。 */
export function List({ children }: { children: ReactNode }) {
  return <div className="bg-card rounded-card shadow-card overflow-hidden">{children}</div>
}

/**
 * 稿子的 `.row`：**整行可点**，最小 64px。
 *
 * 结构上是「一个撑满行高的按钮（.rtap）+ 可选的第二个 44px 按钮（right）并列」，
 * 不是「一行 div 里塞几个小按钮」——后者的可点面积只有那几个小按钮，
 * 而手机上人是拿拇指去戳一整行的。
 *
 * 纵向 padding 放在行本体上而不是行容器上：放在容器上会让第二个按钮被 padding 推小。
 */
export function Row({ onTap, done = false, lead, title, sub, pts, right, ariaLabel }: {
  onTap?: () => void
  /** 已完成态：标题划掉、分值胶囊转绿。 */
  done?: boolean
  /** 行首：emoji / 序号圆点 / 头像。 */
  lead?: ReactNode
  title: ReactNode
  sub?: ReactNode
  /**
   * 分值胶囊。渲染成**行本体的兄弟**，所以传一个按钮进来是安全的
   * （录入页的「分值一点循环改」就是），点它不会连带触发 onTap。
   */
  pts?: ReactNode
  /** 与行本体并列的第二个 44px 按钮（撤销 / 确认 / 更多）。 */
  right?: ReactNode
  ariaLabel?: string
}) {
  const inner = (
    <>
      {lead != null && <span className="shrink-0 min-w-[26px] text-center text-[22px] leading-none">{lead}</span>}
      <span className="flex-1 min-w-0">
        <span className={'block font-semibold text-[15px] truncate ' + (done ? 'text-muted line-through' : '')}>
          {title}
        </span>
        {sub && <span className="block text-faint text-xs mt-0.5">{sub}</span>}
      </span>
    </>
  )

  return (
    <div className="relative flex items-center gap-3 w-full min-h-16 px-3.5 border-b border-line last:border-b-0">
      {onTap ? (
        <button
          type="button"
          onClick={onTap}
          aria-label={ariaLabel}
          className="flex-1 min-w-0 self-stretch flex items-center gap-3 text-left py-[11px] active:bg-sunk"
        >
          {inner}
        </button>
      ) : (
        <span className="flex-1 min-w-0 self-stretch flex items-center gap-3 py-[11px]">{inner}</span>
      )}

      {/* 分值胶囊是**行本体的兄弟**，不在它里面。稿子里就是这样：
          `.rtap` 的 </button> 在 ptsBtn 之前，CSS 也是 `.row .pts{flex:none}`。

          ⚠️ 放进去过一版，代价是两个 bug：
          ① 调用方给 pts 传一个按钮时就是 `<button>` 套 `<button>`（非法 HTML，
             React 报 hydration error）——录入页的「分值一点循环改」正是这么写的；
          ② 更糟的是内层点击会**冒泡**到外层的 onTap。于是在录入页点一下分值，
             那一条会被同时翻成「要求」（分值胶囊跟着消失，因为要求不计分）
             ——「改分值」这个功能实际上一次都没成功过。实测复现过。 */}
      {pts != null && (
        <span
          className={
            'shrink-0 px-[11px] py-[5px] rounded-full font-bold text-[13px] ' +
            (done ? 'bg-green-tint text-green' : 'bg-tint text-brand')
          }
        >
          {pts}
        </span>
      )}
      {right}
    </div>
  )
}

/**
 * 稿子的 `<details class="fold">`：已完成的东西收起来，列表越做越短。
 *
 * 用原生 `<details>` 而不是自己管 open state：折叠状态由浏览器记，
 * Inertia 局部刷新（`only: [...]`）时不会被重置回默认值。
 *
 * 外边距自己带（同 Section 的 mx-3 mt-4）——它本身就是一段，不该再包一层 Section。
 */
export function Fold({ summary, defaultOpen = false, children }: {
  summary: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  return (
    <details open={defaultOpen} className="group mx-3 mt-4">
      <summary className="flex items-center gap-2 min-h-tap px-1 list-none cursor-pointer text-[13px] font-bold text-muted [&::-webkit-details-marker]:hidden">
        <span className="text-faint text-[11px] transition-transform group-open:rotate-90">▶</span>
        {summary}
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  )
}

/** 稿子的 `.bdg` / `.tag`：状态徽标。四个 tone 用的都是 S0 的 `-tint` / `-strong` 组。 */
export function Badge({ tone, children }: {
  tone: 'amber' | 'green' | 'brand' | 'red'
  children: ReactNode
}) {
  const tones = {
    amber: 'bg-amber-tint text-amber-strong',
    green: 'bg-green-tint text-green-strong',
    brand: 'bg-tint text-brand',
    red: 'bg-red-tint text-red-strong',
  }
  return (
    <span className={'shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ' + tones[tone]}>
      {children}
    </span>
  )
}

/** 稿子的 `.banner`：一句要读一下但不用点的话（补打卡、还差一点）。 */
export function Banner({ tone, icon, children }: {
  tone: 'amber' | 'brand'
  icon?: string
  children: ReactNode
}) {
  const tones = {
    amber: 'bg-amber-tint text-amber-strong border-amber-line',
    brand: 'bg-tint text-brand border-tint',
  }
  return (
    <div className={'flex gap-2 items-start mx-3 mt-3 px-3.5 py-[11px] rounded-ctl border text-[13px] leading-[1.4] ' + tones[tone]}>
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{children}</span>
    </div>
  )
}

/** 稿子的 `.btn`：整宽、50px 高、四个变体。主按钮是渐变 + 品牌色发光。 */
export function Button({ variant = 'primary', onClick, disabled, type = 'button', children }: {
  variant?: 'primary' | 'ghost' | 'plain' | 'danger'
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  children: ReactNode
}) {
  const variants = {
    primary: 'bg-gradient-to-br from-brand to-brand2 text-white shadow-btn',
    ghost: 'bg-tint text-brand',
    plain: 'bg-sunk text-muted',
    danger: 'bg-red text-white',
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={
        'flex items-center justify-center gap-[7px] w-full min-h-[50px] rounded-ctl text-base font-bold ' +
        'active:scale-[.985] disabled:opacity-40 disabled:shadow-none disabled:pointer-events-none ' +
        variants[variant]
      }
    >
      {children}
    </button>
  )
}

/** 稿子的 `.field` 里那个输入框的样式。单独导出成字符串而不是包一层组件：
    input / textarea / 数字框各有自己的属性，包起来反而更难用。 */
export const fieldInputClass =
  'w-full min-h-[50px] px-3.5 py-3 rounded-ctl border-[1.5px] border-line bg-sunk text-ink ' +
  'focus:outline-none focus:border-brand focus:bg-card'

/** 稿子的 `.field`：13px 小标题 + 50px 高的输入框。 */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block mb-3.5">
      <span className="block mb-[7px] text-[13px] font-bold text-muted">{label}</span>
      {children}
    </label>
  )
}

/**
 * 稿子的 `.chip`：取代小号数字输入框。
 *
 * 为什么不用 <input type="number">：手机上它要弹数字键盘、点两下、还容易多打一个零，
 * 而 90% 的情况分值就是那几个常用值。chip 旁边仍然留一个「或者自己填一个」的输入框，
 * 剩下 10% 走那里。
 */
export function Chip({ pressed, onClick, children }: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={
        'min-w-14 min-h-tap px-3.5 rounded-ctl border-[1.5px] font-bold text-[15px] ' +
        'flex items-center justify-center ' +
        (pressed ? 'border-brand bg-tint text-brand' : 'border-line bg-sunk')
      }
    >
      {children}
    </button>
  )
}

/**
 * 稿子的 `.seg`：分段控件，每格整块 44px，取代小字 tab。
 *
 * 泛型 T 钉住取值集合：调用点写 `Segments<'30' | '14' | '7'>`，于是 onChange 收到的
 * 是那三个字面量之一而不是 string —— 拿它去索引一张范围表时类型检查会替你抓住拼错。
 */
export function Segments<T extends string>({ options, value, onChange, label }: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="grid grid-flow-col auto-cols-fr gap-0.5 p-[3px] bg-sunk rounded-ctl"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={o.value === value}
          className={
            'min-h-tap rounded-[11px] text-[13px] font-bold ' +
            (o.value === value ? 'bg-card text-ink shadow-card' : 'text-muted')
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
